import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, TrendingUp, Users, UserCheck, FileText, FileSpreadsheet, Award, CheckCircle, XCircle, Settings, Search, Download, AlertTriangle, History, CalendarDays } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const CLASS_TYPES = [
  { value: "all", label: "All Classes" },
  { value: "Tigers Kung Fu", label: "Tigers Kung Fu" },
  { value: "Dragons Kung Fu", label: "Dragons Kung Fu" },
  { value: "Adults Kung Fu", label: "Adults Kung Fu" },
];

// Grading cycles
const GRADING_CYCLES = [
  { value: "jan-jul", label: "January - July", startMonth: 1, endMonth: 7 },
  { value: "sep-dec", label: "September - December", startMonth: 9, endMonth: 12 },
];

// Helper: Get attendance color based on percentage
const getAttendanceColor = (percentage) => {
  if (percentage >= 80) return "text-green-400";
  if (percentage >= 60) return "text-yellow-400";
  if (percentage >= 40) return "text-orange-400";
  return "text-red-400";
};

const getAttendanceBg = (percentage) => {
  if (percentage >= 80) return "bg-green-500";
  if (percentage >= 60) return "bg-yellow-500";
  if (percentage >= 40) return "bg-orange-500";
  return "bg-red-500";
};

// Helper: Get all classes for a student (supports both old and new format)
const getStudentClasses = (student) => {
  if (student.class_types && student.class_types.length > 0) {
    return student.class_types;
  }
  if (student.class_type) {
    return [student.class_type];
  }
  return [];
};

// Helper: Check if student belongs to a class
const studentInClass = (student, classType) => {
  const classes = getStudentClasses(student);
  return classes.includes(classType);
};

// Helper: Format classes for display
const formatStudentClasses = (student) => {
  const classes = getStudentClasses(student);
  return classes.map(c => c.replace(" Kung Fu", "")).join(", ");
};

export default function AttendanceReports() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterClass, setFilterClass] = useState("all");
  const [period, setPeriod] = useState("monthly");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Grading Eligibility State
  const [activeTab, setActiveTab] = useState("reports");
  const [gradingCycle, setGradingCycle] = useState("jan-jul");
  const [gradingYear, setGradingYear] = useState(new Date().getFullYear());
  const [totalClassesHeld, setTotalClassesHeld] = useState(24); // Default 24 classes per cycle
  const [eligibilityThreshold, setEligibilityThreshold] = useState(80); // Default 80%
  const [gradingData, setGradingData] = useState(null);
  const [loadingGrading, setLoadingGrading] = useState(false);

  // Archive Tab State
  const [archiveSearchQuery, setArchiveSearchQuery] = useState("");
  const [archiveStudents, setArchiveStudents] = useState([]);
  const [selectedArchiveStudent, setSelectedArchiveStudent] = useState(null);
  const [archiveHistory, setArchiveHistory] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [archiveStartDate, setArchiveStartDate] = useState("");
  const [archiveEndDate, setArchiveEndDate] = useState("");
  const [archiveQuickFilter, setArchiveQuickFilter] = useState("");

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (activeTab === "reports") {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, selectedMonth, selectedYear, filterClass, activeTab]);

  useEffect(() => {
    if (activeTab === "grading") {
      fetchGradingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradingCycle, gradingYear, filterClass, activeTab]);

  // Load students for archive search
  useEffect(() => {
    if (activeTab === "archive") {
      fetchArchiveStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchArchiveStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`);
      setArchiveStudents(response.data || []);
    } catch (error) {
      console.error("Failed to load students for archive");
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period,
        month: selectedMonth,
        year: selectedYear,
        ...(filterClass !== "all" && { class_type: filterClass })
      });
      
      const response = await axios.get(`${API}/attendance/report?${params}`);
      setReportData(response.data);
    } catch (error) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const fetchGradingData = async () => {
    setLoadingGrading(true);
    try {
      const cycle = GRADING_CYCLES.find(c => c.value === gradingCycle);
      
      // Fetch all students
      const studentsRes = await axios.get(`${API}/students`);
      let students = studentsRes.data;
      
      // Filter by class if needed - check both class_type and class_types array
      if (filterClass !== "all") {
        students = students.filter(s => {
          // Check class_types array first (new multi-class format)
          if (s.class_types && s.class_types.length > 0) {
            return s.class_types.includes(filterClass);
          }
          // Fallback to legacy class_type field
          return s.class_type === filterClass;
        });
      }
      
      // Fetch attendance for the grading period
      const attendanceRes = await axios.get(`${API}/attendance`);
      const allAttendance = attendanceRes.data;
      
      // Filter attendance by grading cycle dates
      const filteredAttendance = allAttendance.filter(a => {
        const dateParts = a.date.split('/');
        if (dateParts.length !== 3) return false;
        const month = parseInt(dateParts[1], 10);
        const year = parseInt(dateParts[2], 10);
        return year === gradingYear && month >= cycle.startMonth && month <= cycle.endMonth;
      });
      
      // Calculate attendance per student
      const studentAttendance = students.map(student => {
        const studentRecords = filteredAttendance.filter(a => a.student_id === student.id);
        const attendanceCount = studentRecords.length;
        const attendancePercentage = totalClassesHeld > 0 
          ? Math.round((attendanceCount / totalClassesHeld) * 100) 
          : 0;
        const isEligible = attendancePercentage >= eligibilityThreshold;
        
        // Check if student is in adult class (using multi-class support)
        const isAdult = studentInClass(student, "Adults Kung Fu");
        
        return {
          ...student,
          attendance_count: attendanceCount,
          attendance_percentage: attendancePercentage,
          is_eligible: isEligible,
          rank: isAdult 
            ? (student.adult_level || student.adult_pillar || "Not Set")
            : student.belt_rank,
          classes_display: formatStudentClasses(student)
        };
      });
      
      // Sort by eligibility then by attendance percentage
      studentAttendance.sort((a, b) => {
        if (a.is_eligible !== b.is_eligible) return b.is_eligible - a.is_eligible;
        return b.attendance_percentage - a.attendance_percentage;
      });
      
      const eligibleCount = studentAttendance.filter(s => s.is_eligible).length;
      
      setGradingData({
        students: studentAttendance,
        total_students: students.length,
        eligible_count: eligibleCount,
        ineligible_count: students.length - eligibleCount,
        cycle_label: cycle.label,
        year: gradingYear
      });
    } catch (error) {
      toast.error("Failed to load grading data");
    } finally {
      setLoadingGrading(false);
    }
  };

  // Generate multi-tab .xlsx attendance report
  const generateXLSX = () => {
    if (!reportData?.students) return;
    const XLSX = require('xlsx');

    const formatDate = (d) => {
      const dt = new Date(d);
      if (isNaN(dt)) return d;
      return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
    };

    // Classify students by class type
    const classify = (s) => {
      const classes = [...(s.class_types || []), s.class_type || ''].join(' ').toLowerCase();
      const result = [];
      if (classes.includes('adults kung fu') || classes.includes('adult')) result.push('ADULTS');
      if (classes.includes('dragon')) result.push('DRAGONS');
      if (classes.includes('tiger')) result.push('TIGERS');
      if (classes.includes('tai chi') || classes.includes('tai-chi')) result.push('TAI CHI');
      if (classes.includes('private') || classes.includes('remote') || classes.includes('international')) result.push('PRIVATE & REMOTE');
      return result.length ? result : ['ADULTS'];
    };

    const tabs = { 'ADULTS': [], 'DRAGONS': [], 'TIGERS': [], 'TAI CHI': [], 'PRIVATE & REMOTE': [] };

    reportData.students.forEach(s => {
      const row = {
        'Name': s.name,
        'Age': s.age || 'N/A',
        'Class': s.class_type,
        'Rank': s.rank,
        'Attendance Count': s.attendance_count,
        'Attendance %': s.attendance_percentage,
      };
      const cats = classify(s);
      cats.forEach(cat => { if (tabs[cat]) tabs[cat].push(row); });
    });

    const wb = XLSX.utils.book_new();

    Object.entries(tabs).forEach(([tabName, rows]) => {
      const total = rows.length;
      const avgPct = total ? (rows.reduce((s, r) => s + (parseFloat(r['Attendance %']) || 0), 0) / total).toFixed(1) : '0';
      const headerRows = [
        { 'Name': `${tabName} — ${reportData.period_label}`, 'Age': '', 'Class': '', 'Rank': '', 'Attendance Count': '', 'Attendance %': '' },
        { 'Name': `Total Students: ${total}`, 'Age': '', 'Class': '', 'Rank': '', 'Attendance Count': '', 'Attendance %': '' },
        { 'Name': `Average Attendance: ${avgPct}%`, 'Age': '', 'Class': '', 'Rank': '', 'Attendance Count': '', 'Attendance %': '' },
        { 'Name': `Generated: ${formatDate(new Date())}`, 'Age': '', 'Class': '', 'Rank': '', 'Attendance Count': '', 'Attendance %': '' },
        { 'Name': '', 'Age': '', 'Class': '', 'Rank': '', 'Attendance Count': '', 'Attendance %': '' },
      ];
      const ws = XLSX.utils.json_to_sheet([...headerRows, ...rows]);
      ws['!cols'] = [{ wch: 28 }, { wch: 8 }, { wch: 22 }, { wch: 18 }, { wch: 18 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, ws, tabName);
    });

    XLSX.writeFile(wb, `attendance_report_${period}_${selectedMonth}_${selectedYear}.xlsx`);
    toast.success("Attendance Excel downloaded");
  };

  // Generate Grading Eligibility CSV
  const generateGradingCSV = () => {
    if (!gradingData?.students) return;
    
    const headers = ["Name", "Class", "Rank", "Sessions Attended", "Total Classes", "Attendance %", "Eligible"];
    const rows = gradingData.students.map(s => [
      s.name,
      s.class_type,
      s.rank,
      s.attendance_count,
      totalClassesHeld,
      `${s.attendance_percentage}%`,
      s.is_eligible ? "YES" : "NO"
    ]);
    
    const csvContent = [
      `Grading Eligibility Report - ${gradingData.cycle_label} ${gradingData.year}`,
      `Generated: ${new Date().toLocaleString()}`,
      `Total Classes Held: ${totalClassesHeld}`,
      `Eligibility Threshold: ${eligibilityThreshold}%`,
      `Total Students: ${gradingData.total_students}`,
      `Eligible: ${gradingData.eligible_count}`,
      `Not Eligible: ${gradingData.ineligible_count}`,
      "",
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `grading_eligibility_${gradingCycle}_${gradingYear}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Grading eligibility CSV downloaded");
  };

  // Generate PDF (simple HTML-based)
  const generatePDF = () => {
    if (!reportData?.students) return;
    
    const printWindow = window.open("", "_blank");
    const tableRows = reportData.students.map(s => `
      <tr>
        <td style="padding: 8px; border: 1px solid #ddd;">${s.name}${s.is_sifu ? ' <span style="background: gold; padding: 2px 4px; font-size: 10px;">SIFU</span>' : ''}</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${s.age || "N/A"}</td>
        <td style="padding: 8px; border: 1px solid #ddd;">${s.class_type}</td>
        <td style="padding: 8px; border: 1px solid #ddd;">${s.rank}</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${s.attendance_count}</td>
        <td style="padding: 8px; border: 1px solid #ddd; text-align: center; font-weight: bold; color: ${s.attendance_percentage >= 70 ? 'green' : 'red'};">${s.attendance_percentage}%</td>
      </tr>
    `).join("");
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Attendance Report - ${reportData.period_label}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; border-bottom: 2px solid #D4AF37; padding-bottom: 10px; }
          .summary { background: #f5f5f5; padding: 15px; margin: 20px 0; border-radius: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #333; color: white; padding: 10px; text-align: left; }
          tr:nth-child(even) { background: #f9f9f9; }
          .footer { margin-top: 30px; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <h1>Loong Fu Tong - Attendance Report</h1>
        <h2>${reportData.period_label}</h2>
        
        <div class="summary">
          <strong>Period:</strong> ${period === 'weekly' ? 'Weekly' : 'Monthly'} |
          <strong>Total Students:</strong> ${reportData.total_students} |
          <strong>Expected Classes:</strong> ${reportData.total_class_days} |
          <strong>Average Attendance:</strong> ${reportData.average_attendance}%
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Age</th>
              <th>Class</th>
              <th>Rank</th>
              <th>Sessions</th>
              <th>Attendance %</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        
        <div class="footer">
          Generated on ${new Date().toLocaleString()} | Loong Fu Tong 龍虎堂
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
    toast.success("PDF generated - use Print to save as PDF");
  };

  // Generate Grading PDF
  const generateGradingPDF = () => {
    if (!gradingData?.students) return;
    
    const printWindow = window.open("", "_blank");
    const tableRows = gradingData.students.map(s => `
      <tr style="background: ${s.is_eligible ? '#e8f5e9' : '#ffebee'};">
        <td style="padding: 10px; border: 1px solid #ddd;">
          ${s.name}
          ${s.is_sifu ? ' <span style="background: gold; padding: 2px 6px; font-size: 10px; border-radius: 3px;">SIFU</span>' : ''}
        </td>
        <td style="padding: 10px; border: 1px solid #ddd;">${s.class_type.replace(" Kung Fu", "")}</td>
        <td style="padding: 10px; border: 1px solid #ddd;">${s.rank}</td>
        <td style="padding: 10px; border: 1px solid #ddd; text-align: center;">${s.attendance_count} / ${totalClassesHeld}</td>
        <td style="padding: 10px; border: 1px solid #ddd; text-align: center; font-weight: bold;">
          ${s.attendance_percentage}%
        </td>
        <td style="padding: 10px; border: 1px solid #ddd; text-align: center;">
          <span style="background: ${s.is_eligible ? '#4caf50' : '#f44336'}; color: white; padding: 4px 12px; border-radius: 4px; font-weight: bold;">
            ${s.is_eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE'}
          </span>
        </td>
      </tr>
    `).join("");
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Grading Eligibility - ${gradingData.cycle_label} ${gradingData.year}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; border-bottom: 2px solid #D4AF37; padding-bottom: 10px; }
          h2 { color: #666; }
          .summary { background: #f5f5f5; padding: 15px; margin: 20px 0; border-radius: 5px; display: flex; gap: 30px; flex-wrap: wrap; }
          .summary-item { }
          .summary-label { color: #666; font-size: 12px; text-transform: uppercase; }
          .summary-value { font-size: 24px; font-weight: bold; }
          .eligible { color: #4caf50; }
          .ineligible { color: #f44336; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #333; color: white; padding: 12px; text-align: left; }
          .footer { margin-top: 30px; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 15px; }
        </style>
      </head>
      <body>
        <h1>龍虎堂 LOONG FU TONG - Grading Eligibility Report</h1>
        <h2>${gradingData.cycle_label} ${gradingData.year}</h2>
        
        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">Total Classes Held</div>
            <div class="summary-value">${totalClassesHeld}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Eligibility Threshold</div>
            <div class="summary-value">${eligibilityThreshold}%</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total Students</div>
            <div class="summary-value">${gradingData.total_students}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Eligible</div>
            <div class="summary-value eligible">${gradingData.eligible_count}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Not Eligible</div>
            <div class="summary-value ineligible">${gradingData.ineligible_count}</div>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Student Name</th>
              <th>Class</th>
              <th>Current Rank</th>
              <th>Sessions</th>
              <th>Attendance %</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        
        <div class="footer">
          <p><strong>Note:</strong> Eligibility is based on ${eligibilityThreshold}% minimum attendance (${Math.ceil(totalClassesHeld * eligibilityThreshold / 100)} sessions required).</p>
          <p>Generated on ${new Date().toLocaleString()} | Loong Fu Martial Arts Management</p>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
    toast.success("Grading eligibility PDF generated");
  };

  // ==========================================
  // ARCHIVE FUNCTIONS
  // ==========================================
  
  // Filter students by search query
  const filteredArchiveStudents = archiveStudents.filter(s => 
    s.name.toLowerCase().includes(archiveSearchQuery.toLowerCase())
  );

  // Set quick filter dates
  const applyQuickFilter = (filter) => {
    setArchiveQuickFilter(filter);
    const today = new Date();
    let start, end;
    
    switch (filter) {
      case "last-month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "last-6-months":
        start = new Date(today.getFullYear(), today.getMonth() - 6, 1);
        end = today;
        break;
      case "ytd":
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;
      case "all":
      default:
        setArchiveStartDate("");
        setArchiveEndDate("");
        if (selectedArchiveStudent) fetchStudentHistory(selectedArchiveStudent.id, "", "");
        return;
    }
    
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    setArchiveStartDate(startStr);
    setArchiveEndDate(endStr);
    
    if (selectedArchiveStudent) {
      fetchStudentHistory(selectedArchiveStudent.id, startStr, endStr);
    }
  };

  // Fetch attendance history for a student
  const fetchStudentHistory = async (studentId, startDate = archiveStartDate, endDate = archiveEndDate) => {
    setLoadingArchive(true);
    try {
      const params = new URLSearchParams({ student_id: studentId });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await axios.get(`${API}/attendance/history?${params}`);
      
      // Calculate gaps
      const history = response.data || [];
      const historyWithGaps = history.map((record, index) => {
        let gapDays = 0;
        if (index < history.length - 1) {
          const currentDate = parseAttendanceDate(record.date);
          const prevDate = parseAttendanceDate(history[index + 1].date);
          if (currentDate && prevDate) {
            gapDays = Math.floor((currentDate - prevDate) / (1000 * 60 * 60 * 24));
          }
        }
        return { ...record, gapDays };
      });
      
      setArchiveHistory(historyWithGaps);
    } catch (error) {
      toast.error("Failed to load attendance history");
      setArchiveHistory([]);
    } finally {
      setLoadingArchive(false);
    }
  };

  // Parse attendance date (DD/MM/YYYY format)
  const parseAttendanceDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
    return new Date(parts[2], parts[1] - 1, parts[0]);
  };

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    const date = parseAttendanceDate(dateStr);
    if (!date) return dateStr;
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  // Select a student for archive viewing
  const selectArchiveStudent = (student) => {
    setSelectedArchiveStudent(student);
    setArchiveSearchQuery(student.name);
    fetchStudentHistory(student.id);
  };

  // Export archive history to CSV
  const exportArchiveCSV = () => {
    if (!archiveHistory.length || !selectedArchiveStudent) return;
    
    const headers = ["Date", "Day", "Class Type", "Check-in Time", "Gap (Days)", "Gap Alert"];
    const rows = archiveHistory.map(record => {
      const date = parseAttendanceDate(record.date);
      const dayName = date ? date.toLocaleDateString('en-GB', { weekday: 'long' }) : '';
      return [
        record.date,
        dayName,
        record.class_type || 'N/A',
        record.check_in_time || 'N/A',
        record.gapDays || 0,
        record.gapDays > 14 ? 'YES' : ''
      ];
    });
    
    const dateRange = archiveStartDate && archiveEndDate 
      ? `${archiveStartDate} to ${archiveEndDate}` 
      : 'All Time';
    
    const csvContent = [
      `Attendance History - ${selectedArchiveStudent.name}`,
      `Date Range: ${dateRange}`,
      `Total Sessions: ${archiveHistory.length}`,
      `Generated: ${new Date().toLocaleString()}`,
      "",
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `attendance_history_${selectedArchiveStudent.name.replace(/\s+/g, '_')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  // Generate month options for the last 12 months
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    let m = currentMonth - i;
    let y = currentYear;
    if (m <= 0) {
      m += 12;
      y -= 1;
    }
    monthOptions.push({ month: m, year: y, label: `${MONTH_NAMES[m - 1]} ${y}` });
  }

  // Year options for grading
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div data-testid="attendance-reports-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 
          className="text-3xl font-bold text-white uppercase tracking-wide"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          Attendance Reports 出席報告
        </h1>
        <p className="text-[#A1A1AA] mt-1">Generate reports, check grading eligibility, and view attendance history</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-[#18181b] border border-white/10 p-1">
          <TabsTrigger 
            value="reports" 
            className="data-[state=active]:bg-[#27272a] data-[state=active]:text-white text-[#A1A1AA]"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Reports
          </TabsTrigger>
          <TabsTrigger 
            value="grading" 
            className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#A1A1AA]"
          >
            <Award className="w-4 h-4 mr-2" />
            Grading
          </TabsTrigger>
          <TabsTrigger 
            value="archive" 
            className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-[#A1A1AA]"
          >
            <History className="w-4 h-4 mr-2" />
            Archive
          </TabsTrigger>
        </TabsList>

        {/* ==========================================
            ATTENDANCE REPORTS TAB
            ========================================== */}
        <TabsContent value="reports" className="space-y-4">
          {/* Export Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={generateXLSX}
              disabled={loading || !reportData}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="export-xlsx-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button
              onClick={generatePDF}
              disabled={loading || !reportData}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>

          {/* Filters */}
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-full sm:w-40 bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Period" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10">
                    <SelectItem value="weekly" className="text-white hover:bg-white/10">Weekly</SelectItem>
                    <SelectItem value="monthly" className="text-white hover:bg-white/10">Monthly</SelectItem>
                  </SelectContent>
                </Select>

                {period === "monthly" && (
                  <Select 
                    value={`${selectedMonth}-${selectedYear}`} 
                    onValueChange={(val) => {
                      const [m, y] = val.split("-").map(Number);
                      setSelectedMonth(m);
                      setSelectedYear(y);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10">
                      {monthOptions.map((opt) => (
                        <SelectItem 
                          key={`${opt.month}-${opt.year}`} 
                          value={`${opt.month}-${opt.year}`} 
                          className="text-white hover:bg-white/10"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Select value={filterClass} onValueChange={setFilterClass}>
                  <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Filter by class" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#27272a] border-white/10">
                    {CLASS_TYPES.map((c) => (
                      <SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/10">{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {reportData && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-[#18181b] border-white/10 rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-sm">
                      <Calendar className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs uppercase">Period</p>
                      <p className="text-white font-bold">{reportData.period_label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#18181b] border-white/10 rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-sm">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs uppercase">Students</p>
                      <p className="text-white font-bold text-xl">{reportData.total_students}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#18181b] border-white/10 rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-500/20 rounded-sm">
                      <UserCheck className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs uppercase">Class Days</p>
                      <p className="text-white font-bold text-xl">{reportData.total_class_days}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#18181b] border-white/10 rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-sm">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs uppercase">Avg Attendance</p>
                      <p className={`font-bold text-xl ${getAttendanceColor(reportData.average_attendance)}`}>
                        {reportData.average_attendance}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Report Table */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-[#A1A1AA]">Loading report...</div>
            </div>
          ) : reportData?.students?.length === 0 ? (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-8 text-center">
                <p className="text-[#A1A1AA]">No attendance data for this period</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-4">
                <CardTitle className="text-base font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Student Attendance Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">#</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Name</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider w-16 text-center">Age</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Class</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Rank</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider w-24 text-center">Sessions</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider w-32 text-center">Attendance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData?.students?.map((student, index) => (
                        <TableRow 
                          key={student.student_id} 
                          className="border-white/5 hover:bg-white/5"
                        >
                          <TableCell className="text-[#A1A1AA] text-sm">{index + 1}</TableCell>
                          <TableCell className="font-medium text-white">
                            <div className="flex items-center gap-2">
                              <span>{student.name}</span>
                              {student.is_sifu && (
                                <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded-sm bg-[#D4AF37] text-black font-bold">
                                  師父
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-[#A1A1AA]">
                            {student.age || "—"}
                          </TableCell>
                          <TableCell className="text-[#A1A1AA]">
                            {student.class_type.replace(" Kung Fu", "")}
                          </TableCell>
                          <TableCell className="text-[#A1A1AA] text-sm">
                            {student.rank || "—"}
                          </TableCell>
                          <TableCell className="text-center text-white font-medium">
                            {student.attendance_count}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              <span className={`font-bold ${getAttendanceColor(student.attendance_percentage)}`}>
                                {student.attendance_percentage}%
                              </span>
                              <Progress 
                                value={student.attendance_percentage} 
                                className="h-1.5 w-20 bg-white/10"
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==========================================
            GRADING ELIGIBILITY TAB
            ========================================== */}
        <TabsContent value="grading" className="space-y-4">
          {/* Settings Card */}
          <Card className="bg-[#18181b] border-[#D4AF37]/30 rounded-sm">
            <CardHeader className="border-b border-white/5 p-4">
              <CardTitle className="text-base font-semibold text-[#D4AF37] uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <Settings className="w-4 h-4" />
                Grading Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Grading Cycle */}
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Grading Cycle</Label>
                  <Select value={gradingCycle} onValueChange={setGradingCycle}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10">
                      {GRADING_CYCLES.map((c) => (
                        <SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/10">
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Year */}
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Year</Label>
                  <Select value={gradingYear.toString()} onValueChange={(v) => setGradingYear(parseInt(v))}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10">
                      {yearOptions.map((y) => (
                        <SelectItem key={y} value={y.toString()} className="text-white hover:bg-white/10">
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Total Classes Held (Manual Override) */}
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Total Classes Held</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={totalClassesHeld}
                    onChange={(e) => setTotalClassesHeld(parseInt(e.target.value) || 1)}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>

                {/* Eligibility Threshold */}
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Threshold %</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={eligibilityThreshold}
                    onChange={(e) => setEligibilityThreshold(parseInt(e.target.value) || 80)}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>

                {/* Class Filter */}
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Class</Label>
                  <Select value={filterClass} onValueChange={setFilterClass}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#27272a] border-white/10">
                      {CLASS_TYPES.map((c) => (
                        <SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/10">
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4">
                <Button
                  onClick={fetchGradingData}
                  className="bg-[#D4AF37] hover:bg-[#b8962e] text-black"
                >
                  Calculate Eligibility
                </Button>
                <Button
                  onClick={generateGradingCSV}
                  disabled={!gradingData}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  onClick={generateGradingPDF}
                  disabled={!gradingData}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          {gradingData && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-[#18181b] border-white/10 rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#D4AF37]/20 rounded-sm">
                      <Award className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs uppercase">Grading Period</p>
                      <p className="text-white font-bold">{gradingData.cycle_label} {gradingData.year}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#18181b] border-white/10 rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-sm">
                      <Users className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs uppercase">Total Students</p>
                      <p className="text-white font-bold text-xl">{gradingData.total_students}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#18181b] border-green-500/30 rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-sm">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs uppercase">Eligible</p>
                      <p className="text-green-400 font-bold text-xl">{gradingData.eligible_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#18181b] border-red-500/30 rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/20 rounded-sm">
                      <XCircle className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <p className="text-[#A1A1AA] text-xs uppercase">Not Eligible</p>
                      <p className="text-red-400 font-bold text-xl">{gradingData.ineligible_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Grading Table */}
          {loadingGrading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-[#A1A1AA]">Calculating eligibility...</div>
            </div>
          ) : !gradingData ? (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-8 text-center">
                <Award className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">Select a grading cycle and click "Calculate Eligibility" to see results</p>
              </CardContent>
            </Card>
          ) : gradingData?.students?.length === 0 ? (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-8 text-center">
                <p className="text-[#A1A1AA]">No students found for this period</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-4">
                <CardTitle className="text-base font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Grading Eligibility ({gradingData.eligible_count} of {gradingData.total_students} eligible)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">#</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Student</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Class</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Current Rank</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider text-center">Sessions</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider text-center">Attendance</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider text-center">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gradingData?.students?.map((student, index) => (
                        <TableRow 
                          key={student.id} 
                          className={`border-white/5 ${student.is_eligible ? 'bg-green-500/5 hover:bg-green-500/10' : 'bg-red-500/5 hover:bg-red-500/10'}`}
                        >
                          <TableCell className="text-[#A1A1AA] text-sm">{index + 1}</TableCell>
                          <TableCell className="font-medium text-white">
                            <div className="flex items-center gap-2">
                              <span>{student.name}</span>
                              {student.is_sifu && (
                                <span className="inline-flex px-1.5 py-0.5 text-[10px] rounded-sm bg-[#D4AF37] text-black font-bold">
                                  師父
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-[#A1A1AA]">
                            {student.class_type.replace(" Kung Fu", "")}
                          </TableCell>
                          <TableCell className="text-[#A1A1AA] text-sm">
                            {student.rank || "—"}
                          </TableCell>
                          <TableCell className="text-center text-white">
                            <span className="font-medium">{student.attendance_count}</span>
                            <span className="text-[#A1A1AA]"> / {totalClassesHeld}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              <span className={`font-bold ${student.is_eligible ? 'text-green-400' : 'text-red-400'}`}>
                                {student.attendance_percentage}%
                              </span>
                              <Progress 
                                value={student.attendance_percentage} 
                                className="h-1.5 w-20 bg-white/10"
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {student.is_eligible ? (
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-sm bg-green-500 text-white text-xs font-bold">
                                <CheckCircle className="w-3 h-3" />
                                ELIGIBLE
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-sm bg-red-500 text-white text-xs font-bold">
                                <XCircle className="w-3 h-3" />
                                NOT ELIGIBLE
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Note */}
          <Card className="bg-[#18181b] border-white/10 rounded-sm">
            <CardContent className="p-4">
              <p className="text-[#A1A1AA] text-sm">
                <strong className="text-[#D4AF37]">Note:</strong> Students need at least {eligibilityThreshold}% attendance 
                ({Math.ceil(totalClassesHeld * eligibilityThreshold / 100)} of {totalClassesHeld} classes) to be eligible for grading.
                Adjust the "Total Classes Held" to match the actual number of classes conducted during the grading period.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==========================================
            ATTENDANCE ARCHIVE TAB
            ========================================== */}
        <TabsContent value="archive" className="space-y-4">
          {/* Search and Filters */}
          <Card className="bg-[#18181b] border-blue-500/30 rounded-sm">
            <CardHeader className="border-b border-white/5 p-4">
              <CardTitle className="text-base font-semibold text-blue-400 uppercase tracking-wide flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <History className="w-4 h-4" />
                Student Attendance Archive
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Student Search */}
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Search Student</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                  <Input
                    type="text"
                    placeholder="Type student name..."
                    value={archiveSearchQuery}
                    onChange={(e) => {
                      setArchiveSearchQuery(e.target.value);
                      if (e.target.value === "") {
                        setSelectedArchiveStudent(null);
                        setArchiveHistory([]);
                      }
                    }}
                    className="pl-10 bg-white/5 border-white/10 text-white"
                    data-testid="archive-student-search"
                  />
                </div>
                {/* Student dropdown */}
                {archiveSearchQuery && !selectedArchiveStudent && filteredArchiveStudents.length > 0 && (
                  <div className="bg-[#27272a] border border-white/10 rounded-sm max-h-48 overflow-y-auto">
                    {filteredArchiveStudents.slice(0, 10).map(student => (
                      <button
                        key={student.id}
                        onClick={() => selectArchiveStudent(student)}
                        className="w-full text-left px-4 py-2 hover:bg-white/10 text-white text-sm flex items-center justify-between"
                      >
                        <span>{student.name}</span>
                        <span className="text-[#A1A1AA] text-xs">{student.class_type?.replace(" Kung Fu", "")}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Date Filters */}
              {selectedArchiveStudent && (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA] text-xs uppercase">Start Date</Label>
                      <Input
                        type="date"
                        value={archiveStartDate}
                        onChange={(e) => setArchiveStartDate(e.target.value)}
                        className="bg-white/5 border-white/10 text-white w-40"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA] text-xs uppercase">End Date</Label>
                      <Input
                        type="date"
                        value={archiveEndDate}
                        onChange={(e) => setArchiveEndDate(e.target.value)}
                        className="bg-white/5 border-white/10 text-white w-40"
                      />
                    </div>
                    <Button
                      onClick={() => fetchStudentHistory(selectedArchiveStudent.id)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </div>

                  {/* Quick Filters */}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-[#A1A1AA] text-xs uppercase self-center mr-2">Quick:</span>
                    <Button
                      variant={archiveQuickFilter === "last-month" ? "default" : "outline"}
                      size="sm"
                      onClick={() => applyQuickFilter("last-month")}
                      className={archiveQuickFilter === "last-month" ? "bg-blue-600" : "border-white/20 text-white hover:bg-white/10"}
                    >
                      Last Month
                    </Button>
                    <Button
                      variant={archiveQuickFilter === "last-6-months" ? "default" : "outline"}
                      size="sm"
                      onClick={() => applyQuickFilter("last-6-months")}
                      className={archiveQuickFilter === "last-6-months" ? "bg-blue-600" : "border-white/20 text-white hover:bg-white/10"}
                    >
                      Last 6 Months
                    </Button>
                    <Button
                      variant={archiveQuickFilter === "ytd" ? "default" : "outline"}
                      size="sm"
                      onClick={() => applyQuickFilter("ytd")}
                      className={archiveQuickFilter === "ytd" ? "bg-blue-600" : "border-white/20 text-white hover:bg-white/10"}
                    >
                      Year to Date
                    </Button>
                    <Button
                      variant={archiveQuickFilter === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => applyQuickFilter("all")}
                      className={archiveQuickFilter === "all" ? "bg-blue-600" : "border-white/20 text-white hover:bg-white/10"}
                    >
                      All Time
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Selected Student Info & Export */}
          {selectedArchiveStudent && (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-sm">
                  <UserCheck className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-bold">{selectedArchiveStudent.name}</p>
                  <p className="text-[#A1A1AA] text-sm">
                    {selectedArchiveStudent.class_type} • {archiveHistory.length} sessions found
                  </p>
                </div>
              </div>
              <Button
                onClick={exportArchiveCSV}
                disabled={archiveHistory.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="archive-export-csv"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          )}

          {/* Archive Results Table */}
          {loadingArchive ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-[#A1A1AA]">Loading attendance history...</div>
            </div>
          ) : !selectedArchiveStudent ? (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-8 text-center">
                <Search className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <p className="text-[#A1A1AA]">Search for a student to view their attendance history</p>
              </CardContent>
            </Card>
          ) : archiveHistory.length === 0 ? (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-8 text-center">
                <CalendarDays className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">No attendance records found for the selected period</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardHeader className="border-b border-white/5 p-4">
                <CardTitle className="text-base font-semibold text-white uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Attendance History ({archiveHistory.length} sessions)
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">#</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Date</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Day</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Class</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider">Check-in</TableHead>
                        <TableHead className="text-[#A1A1AA] text-xs uppercase tracking-wider text-center">Gap</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archiveHistory.map((record, index) => {
                        const date = parseAttendanceDate(record.date);
                        const dayName = date ? date.toLocaleDateString('en-GB', { weekday: 'short' }) : '';
                        const hasGapAlert = record.gapDays > 14;
                        
                        return (
                          <TableRow 
                            key={record.id} 
                            className={`border-white/5 ${hasGapAlert ? 'bg-orange-500/10' : 'hover:bg-white/5'}`}
                          >
                            <TableCell className="text-[#A1A1AA] text-sm">{index + 1}</TableCell>
                            <TableCell className="font-medium text-white">
                              {formatDisplayDate(record.date)}
                            </TableCell>
                            <TableCell className="text-[#A1A1AA]">{dayName}</TableCell>
                            <TableCell className="text-[#A1A1AA]">
                              {record.class_type?.replace(" Kung Fu", "") || "—"}
                            </TableCell>
                            <TableCell className="text-[#A1A1AA]">
                              {record.check_in_time || "—"}
                            </TableCell>
                            <TableCell className="text-center">
                              {index < archiveHistory.length - 1 ? (
                                <div className="flex items-center justify-center gap-1">
                                  {hasGapAlert && (
                                    <AlertTriangle className="w-4 h-4 text-orange-400" />
                                  )}
                                  <span className={hasGapAlert ? "text-orange-400 font-bold" : "text-[#A1A1AA]"}>
                                    {record.gapDays}d
                                  </span>
                                </div>
                              ) : (
                                <span className="text-[#A1A1AA]">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Legend */}
          {archiveHistory.length > 0 && (
            <Card className="bg-[#18181b] border-white/10 rounded-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-400" />
                    <span className="text-[#A1A1AA]">Gap Alert: More than 14 days since previous session</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
